import React, { useEffect, useState } from 'react';
import { Check, Palette } from 'lucide-react';
import { getAuthToken, useAuth } from '../../auth/context/AuthContext';
import {
  DEFAULT_BRAND_COLOR,
  brandButtonStyle,
  isHexColor,
  normaliseHex,
  readableTextOn,
} from '../../../shared/utils/brandColor';

const API = `${process.env.REACT_APP_API_URL || 'https://recruitai.pingitright.com/api'}/auth`;

/**
 * The colours the embedded job board is drawn in.
 *
 * The board sits on the customer's own careers page, where our pink reads as
 * somebody else's brand. An admin or manager picks a colour here; everyone
 * else sees what was chosen. The label follows the background unless they say
 * otherwise — a pale brand colour would otherwise carry unreadable white text.
 */

const PRESETS = [
  { color: '#640d5f', label: 'Recruiter24x7' },
  { color: '#2563eb', label: 'Blue' },
  { color: '#0e7a52', label: 'Green' },
  { color: '#b45309', label: 'Amber' },
  { color: '#b42318', label: 'Red' },
  { color: '#334155', label: 'Slate' },
];

const TEXT_CHOICES = [
  { value: '', label: 'Auto' },
  { value: '#ffffff', label: 'Light' },
  { value: '#111827', label: 'Dark' },
];

export default function BrandColorCard() {
  const { user, setUser } = useAuth();
  const mayEdit = ['admin', 'super_admin', 'manager'].includes(user?.role);

  const [color, setColor] = useState(user?.brand_color || DEFAULT_BRAND_COLOR);
  const [textColor, setTextColor] = useState(user?.brand_text_color || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    setColor(user?.brand_color || DEFAULT_BRAND_COLOR);
    setTextColor(user?.brand_text_color || '');
  }, [user?.brand_color, user?.brand_text_color]);

  const save = async (nextColor, nextText = textColor) => {
    const cleaned = normaliseHex(nextColor);
    if (!cleaned) { setErr('Enter a colour like #2563EB'); return; }
    setSaving(true); setErr(''); setSaved(false);
    try {
      const res = await fetch(`${API}/brand-color`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
        body: JSON.stringify({ color: cleaned, text_color: nextText || null }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not save the colour');
      setUser((u) => ({ ...u, brand_color: data.brand_color, brand_text_color: data.brand_text_color }));
      setColor(data.brand_color || DEFAULT_BRAND_COLOR);
      setTextColor(data.brand_text_color || '');
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setErr(e.message);
    }
    setSaving(false);
  };

  // What the buttons will look like, including a colour still being typed.
  const chosen = normaliseHex(color) || DEFAULT_BRAND_COLOR;
  const preview = { ...brandButtonStyle(chosen), ...(textColor ? { color: textColor } : {}) };
  const autoLabel = readableTextOn(chosen) === '#ffffff' ? 'light' : 'dark';

  return (
    <div className="card" style={{ marginTop: 20 }}>
      <div className="card-header">
        <div className="section-title" style={{ marginBottom: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Palette size={16} strokeWidth={2} /> Job board colour
        </div>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          {mayEdit ? 'Used for the buttons on your embedded job board' : 'Set by an admin or manager'}
        </span>
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        {PRESETS.map((preset) => {
          const active = chosen.toLowerCase() === preset.color;
          return (
            <button
              key={preset.color}
              type="button"
              title={preset.label}
              aria-label={preset.label}
              disabled={!mayEdit || saving}
              onClick={() => { setColor(preset.color); save(preset.color); }}
              style={{
                width: 34, height: 34, borderRadius: 9, cursor: mayEdit ? 'pointer' : 'default',
                background: preset.color,
                border: active ? '2px solid var(--text-heading)' : '1px solid var(--border)',
                boxShadow: active ? '0 0 0 3px var(--accent-glow)' : 'none',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              {active && <Check size={16} strokeWidth={3} color={readableTextOn(preset.color)} />}
            </button>
          );
        })}

        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginLeft: 4 }}>
          <input
            type="color"
            value={chosen}
            disabled={!mayEdit || saving}
            onChange={(e) => setColor(e.target.value)}
            onBlur={(e) => mayEdit && save(e.target.value)}
            style={{ width: 40, height: 34, padding: 0, border: '1px solid var(--border)', borderRadius: 9, background: 'none', cursor: mayEdit ? 'pointer' : 'default' }}
            aria-label="Custom colour"
          />
          <input
            className="form-input"
            value={color}
            maxLength={9}
            disabled={!mayEdit || saving}
            // Pasting from a brand guide gives all sorts: FF0000, #ff0000,
            // with spaces. Nothing is rejected while it is still being typed.
            onChange={(e) => { setColor(e.target.value); setErr(''); }}
            onBlur={(e) => mayEdit && save(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
            placeholder="#2563EB"
            style={{ width: 120, fontFamily: 'var(--font-mono)', fontSize: 13 }}
          />
        </label>

        {saving && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Saving…</span>}
        {saved && (
          <span style={{ fontSize: 12, color: 'var(--green)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <Check size={13} strokeWidth={2.5} /> Saved
          </span>
        )}
      </div>

      {err && <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 8 }}>{err}</div>}

      {/* Button text: readable by default, overridable when a brand demands it. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Button text</span>
        <div className="view-switch">
          {TEXT_CHOICES.map((choice) => (
            <button
              key={choice.label}
              type="button"
              disabled={!mayEdit || saving}
              className={textColor === choice.value ? 'active' : ''}
              onClick={() => { setTextColor(choice.value); save(color, choice.value); }}
            >
              {choice.label}
            </button>
          ))}
        </div>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          {textColor ? 'Fixed, whatever the background' : `Follows the background — ${autoLabel} on this colour`}
        </span>
      </div>

      {/* What the candidate will see. */}
      <div style={{ marginTop: 16, padding: 16, border: '1px solid var(--border)', borderRadius: 10, background: '#fff' }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: 'var(--text-faint)', marginBottom: 10 }}>
          Preview
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <button type="button" disabled style={{
            padding: '9px 18px', fontSize: 14, fontWeight: 600, borderRadius: 8, border: 'none', ...preview,
          }}>
            Apply for this role
          </button>
          <button type="button" disabled style={{
            padding: '9px 18px', fontSize: 14, fontWeight: 600, borderRadius: 8,
            background: '#fff', color: '#111827', border: '1px solid #e5e7eb',
          }}>
            View details
          </button>
        </div>
      </div>

      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 10 }}>
        The job board already embedded on your site picks this up on its next load —
        there is no need to paste the code again.
      </div>
    </div>
  );
}
